import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

export interface FotoDTO {
  id: string;
  parent_id: string;
  s3_key: string;
  filename: string;
  mime_type: string;
  size_bytes: number | null;
  created_at: string;
}

// ── Query Keys ──────────────────────────────────────────────────

const planillaFotoKeys = {
  all: ['planilla-fotos'] as const,
  list: (planillaId: string) => [...planillaFotoKeys.all, 'list', planillaId] as const,
};

const extraFotoKeys = {
  all: ['extra-fotos'] as const,
  list: (extraId: string) => [...extraFotoKeys.all, 'list', extraId] as const,
};

// ── Planilla Fotos ──────────────────────────────────────────────

export function usePlanillaFotos(planillaId: string) {
  return useQuery({
    queryKey: planillaFotoKeys.list(planillaId),
    queryFn: async () => {
      const res = await api.get<{ data: FotoDTO[] }>(`/planillas/${planillaId}/fotos`);
      return res.data.data;
    },
    enabled: !!planillaId,
  });
}

export function useCreatePlanillaFoto(planillaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { s3Key: string; filename: string; mimeType: string; sizeBytes?: number }) => {
      const res = await api.post<{ data: FotoDTO }>(`/planillas/${planillaId}/fotos`, data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planillaFotoKeys.list(planillaId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.planillas.detail(planillaId) });
    },
  });
}

export function useDeletePlanillaFoto(planillaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { fotoId: string }) => {
      await api.delete(`/planilla-fotos/${vars.fotoId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planillaFotoKeys.list(planillaId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.planillas.detail(planillaId) });
    },
  });
}

// ── Extra Fotos ─────────────────────────────────────────────────

export function useExtraFotos(extraId: string) {
  return useQuery({
    queryKey: extraFotoKeys.list(extraId),
    queryFn: async () => {
      const res = await api.get<{ data: FotoDTO[] }>(`/extras/${extraId}/fotos`);
      return res.data.data;
    },
    enabled: !!extraId,
  });
}

export function useCreateExtraFoto(extraId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { s3Key: string; filename: string; mimeType: string; sizeBytes?: number }) => {
      const res = await api.post<{ data: FotoDTO }>(`/extras/${extraId}/fotos`, data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: extraFotoKeys.list(extraId) });
    },
  });
}

export function useDeleteExtraFoto(extraId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { fotoId: string }) => {
      await api.delete(`/extra-fotos/${vars.fotoId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: extraFotoKeys.list(extraId) });
    },
  });
}
